import type {ReactElement} from 'react';
import {useRef} from 'react';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import {MediaControlsItem} from '@Components/contextual-menu/components/media-controls-item';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import styles from './media-controls.module.scss';

interface MediaControlsProps {
  className?: string;
  items: Array<MediaControlsItemProps>;
  columns?: 2 | 3;

  onClick?(id: string): void;
}

const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 239,
  },
  large: {
    minWidth: 240,
  },
};

export function MediaControls({className = '', onClick = $.noop, columns = 3, ...props}: MediaControlsProps) {
  const containerRef = useRef<HTMLUListElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);

  const getContainerClasses = (): string => {
    const classes = [styles.container, className];

    if (params.small) {
      classes.push('flex-column-justify-center');
    } else {
      classes.push(columns === 3 ? styles.gridContainerThreeColumns : styles.gridContainer);
    }

    return classes.join(' ');
  };

  const isOnlyElementInRow = (index: number): boolean => {
    if (columns === 3) {
      return false;
    }
    return index === 2 && props.items.length === 3;
  };

  const getItemBorderClasses = (index: number, length: number): string => {
    if (columns === 3) {
      if (index === 0) return `${styles.borderRadiusBottomLeft} ${styles.borderRight} ${styles.borderTop}`;
      if (index === 1) return `${styles.borderRight} ${styles.borderTop}`;
      if (index === 2) return `${styles.borderRadiusBottomRight} ${styles.borderTop}`;
      return '';
    }
    if (index === 0) {
      if (length === 2) {
        return `${styles.borderRadiusBottomLeft} ${styles.borderRight} ${styles.borderTop}`;
      }
      return `${styles.borderRight} ${styles.borderRadiusTopLeft}`;
    }
    if (index === 1) {
      if (length === 2) {
        return `${styles.borderRadiusBottomRight} ${styles.borderTop}`;
      }
      return styles.borderRadiusTopRight;
    }
    if (index === 2) {
      if (length === 3) {
        return `${styles.borderTop} ${styles.borderRadiusBottomLeft} ${styles.borderRadiusBottomRight}`;
      }
      return `${styles.borderTop} ${styles.borderRight} ${styles.borderRadiusBottomLeft}`;
    }
    if (index === 3) {
      return `${styles.borderTop} ${styles.borderRadiusBottomRight}`;
    }
    return '';
  };

  const getItemClasses = (index: number): string => {
    return [
      params.large && isOnlyElementInRow(index) ? styles.onlyElement : '',
      params.large ? getItemBorderClasses(index, props.items.length) : '',
      params.small && index + 1 !== props.items.length ? 'spacing-m-b-1' : '',
    ].join(' ');
  };

  const displayControlItems = (): ReactElement[] => {
    return props.items.map((item: MediaControlsItemProps, index: number) => {
      return (
        <MediaControlsItem
          id={item.id}
          key={item.id}
          text={item.text}
          className={`${item.className ?? ''} ${getItemClasses(index)}`}
          icon={item.icon}
          isLoading={item.isLoading}
          loaderType={item.loaderType}
          isDisabled={item.isDisabled}
          isSelected={item.isSelected}
          textColorClasses={item.textColorClasses}
          textPaddingClasses={item.textPaddingClasses}
          iconClasses={item.iconClasses}
          isTextBold={item.isTextBold}
          tag={item.tag}
          iconContainerClasses={item.iconContainerClasses}
          loadingText={item.loadingText}
          compactIconPadding={columns === 3}
          showAsListItem={params.small}
          onClick={(id): void => {
            if (item.isDisabled || item.isLoading) {
              return;
            }
            if (item.onClick) {
              item.onClick(id);
            }
            onClick(id);
          }}
          tagContainerClassName={item.tagContainerClassName}
          customIconObject={item.customIconObject}
          customPopup={item.customPopup}
          dropdownContent={item.dropdownContent}
          dropdownAlignment={item.dropdownAlignment}
          onDropdownOpen={(): void => {
            if (item.onDropdownOpen) {
              item.onDropdownOpen();
            }
          }}
          disabledTooltipText={item.disabledTooltipText}
          hoverTag={item.hoverTag}
          isHoverTagAlwaysVisible={item.isHoverTagAlwaysVisible}
        />
      );
    });
  };

  return (
    <ul ref={containerRef} className={getContainerClasses()}>
      {displayControlItems()}
    </ul>
  );
}
